import { matchesUrlPattern } from "./aggregates";
import type { Rng } from "./seed";
import type {
  PreviewDevice,
  PreviewSession,
  PreviewSurvey,
  PreviewSurveyAggregate,
  PreviewSurveyAnswer,
  PreviewSurveyImpression,
  PreviewSurveyQuestion,
} from "./types";

const DAY_MS = 24 * 60 * 60 * 1000;

export const PREVIEW_SURVEYS_BUILD_ARGS = {
  // Cap how many sessions per survey are sampled as impressions, to keep
  // aggregates realistic (not every visitor sees a survey).
  IMPRESSION_RATE: 0.45,
} as const;

// Stable pseudo-UUIDs prefixed by 1103e/1103f/1103g for surveys/questions/etc.
const SURVEY_IDS = {
  patient_nps: "11030000-0000-4000-8000-000000000001",
  appointment_experience: "11030000-0000-4000-8000-000000000002",
  insurance_feedback: "11030000-0000-4000-8000-000000000003",
  provider_review_draft: "11030000-0000-4000-8000-000000000004",
} as const;

const QUESTION_IDS = {
  patient_nps_score: "11030001-0000-4000-8000-000000000001",
  appt_ease: "11030002-0000-4000-8000-000000000001",
  appt_repeat: "11030002-0000-4000-8000-000000000002",
  appt_comments: "11030002-0000-4000-8000-000000000003",
  insurance_found: "11030003-0000-4000-8000-000000000001",
  insurance_plan: "11030003-0000-4000-8000-000000000002",
  provider_review_rating: "11030004-0000-4000-8000-000000000001",
  provider_review_recommend: "11030004-0000-4000-8000-000000000002",
  provider_review_comments: "11030004-0000-4000-8000-000000000003",
} as const;

function buildSurveyDefinitions(now: number): PreviewSurvey[] {
  const patientNps: PreviewSurvey = {
    id: SURVEY_IDS.patient_nps,
    name: "Patient NPS",
    description:
      "Net Promoter Score across all engaged visitors on the Meridian site.",
    status: "active",
    trigger: {
      mode: "page",
      matchType: "contains",
      patterns: ["/"],
      eventNames: [],
    },
    idleTimeSeconds: 30,
    startDate: now - 30 * DAY_MS,
    endDate: null,
    createdAt: now - 32 * DAY_MS,
    updatedAt: now - 30 * DAY_MS,
    responseRate: 0.22,
    // Engagement survey — bouncers don't get to score the experience.
    sessionFilter: (s) => s.outcome !== "bounce",
    appearance: {
      position: "bottom_right",
      theme: {
        buttonColor: "#3F51B5",
        backgroundColor: "#FFFFFF",
        textColor: "#1E2125",
      },
    },
    questions: [
      {
        id: QUESTION_IDS.patient_nps_score,
        position: 0,
        type: "nps",
        text: "How likely are you to recommend Meridian Health to a friend or family member?",
        // 0..10 weights: heavy promoter skew (healthcare CSAT pattern).
        // Promoters (9–10) ~55%, Passives (7–8) ~30%, Detractors (0–6) ~15%.
        scoreWeights: [1, 1, 1, 1, 2, 3, 6, 13, 17, 25, 30],
      },
    ],
  };

  const appointmentExperience: PreviewSurvey = {
    id: SURVEY_IDS.appointment_experience,
    name: "Appointment booking experience",
    description:
      "Post-booking feedback collected on the appointment confirmation page.",
    status: "active",
    trigger: {
      mode: "page",
      matchType: "contains",
      patterns: ["/appointments/new/confirmation"],
      eventNames: [],
    },
    idleTimeSeconds: 5,
    startDate: now - 21 * DAY_MS,
    endDate: null,
    createdAt: now - 24 * DAY_MS,
    updatedAt: now - 21 * DAY_MS,
    responseRate: 0.42,
    // Post-conversion survey — only fires for sessions that booked.
    sessionFilter: (s) =>
      s.events.some((e) => e.eventName === "appointment_booking_completed"),
    appearance: {
      position: "bottom_right",
      theme: {
        buttonColor: "#0EA5E9",
        backgroundColor: "#FFFFFF",
        textColor: "#0F172A",
      },
    },
    questions: [
      {
        id: QUESTION_IDS.appt_ease,
        position: 0,
        type: "rating",
        text: "How easy was it to schedule your appointment?",
        lowLabel: "Very difficult",
        highLabel: "Very easy",
        scoreWeights: [3, 5, 12, 30, 50],
      },
      {
        id: QUESTION_IDS.appt_repeat,
        position: 1,
        type: "yes_no",
        text: "Would you book through this site again?",
        yesWeight: 0.88,
      },
      {
        id: QUESTION_IDS.appt_comments,
        position: 2,
        type: "open_text",
        text: "Anything else you'd like us to know?",
        placeholder: "Optional",
        samplePool: [
          "The whole flow was smooth, thank you.",
          "Wish there were more evening slots available.",
          "Easy to find my provider and pick a time.",
          "Confirmation email arrived almost instantly.",
          "Took me a couple tries to find the right specialty.",
          "Loved being able to see provider photos and bios.",
          "Insurance verification was painless.",
          "Could not figure out how to add a second patient.",
          "Great experience overall — already told my partner.",
        ],
      },
    ],
  };

  const insuranceFeedback: PreviewSurvey = {
    id: SURVEY_IDS.insurance_feedback,
    name: "Insurance page feedback",
    description:
      "Did visitors successfully find their plan on the insurance page?",
    status: "paused",
    trigger: {
      mode: "page",
      matchType: "contains",
      patterns: ["/insurance"],
      eventNames: [],
    },
    idleTimeSeconds: 20,
    startDate: now - 60 * DAY_MS,
    endDate: now - 20 * DAY_MS,
    createdAt: now - 62 * DAY_MS,
    updatedAt: now - 20 * DAY_MS,
    responseRate: 0.18,
    appearance: {
      position: "bottom_left",
      theme: {
        buttonColor: "#16A34A",
        backgroundColor: "#FFFFFF",
        textColor: "#0F172A",
      },
    },
    questions: [
      {
        id: QUESTION_IDS.insurance_found,
        position: 0,
        type: "single_choice",
        text: "Were you able to find your insurance plan?",
        choices: [
          { label: "Yes", weight: 60 },
          { label: "Not sure", weight: 25 },
          { label: "No", weight: 15 },
        ],
      },
      {
        id: QUESTION_IDS.insurance_plan,
        position: 1,
        type: "open_text",
        text: "Which plan were you looking for?",
        placeholder: "e.g. Blue Cross PPO",
        samplePool: [
          "Blue Cross Blue Shield PPO",
          "United Healthcare Choice",
          "Aetna HMO",
          "Cigna Open Access",
          "Humana Gold Plus",
          "Medicare Advantage",
          "Anthem HealthKeepers",
          "Kaiser Permanente",
          "Tricare Select",
        ],
      },
    ],
  };

  const providerReviewDraft: PreviewSurvey = {
    id: SURVEY_IDS.provider_review_draft,
    name: "Provider visit review",
    description:
      "Post-visit provider review survey — drafted, not yet published.",
    status: "draft",
    trigger: {
      mode: "page",
      matchType: "contains",
      patterns: ["/providers/"],
      eventNames: [],
    },
    idleTimeSeconds: 15,
    // Drafts have no scheduled run window until they're published.
    startDate: null,
    endDate: null,
    createdAt: now - 3 * DAY_MS,
    updatedAt: now - 1 * DAY_MS,
    // Drafts produce no impressions in `buildSurveys`, so this is a forward-
    // looking estimate rather than something the aggregate ever exercises.
    responseRate: 0.35,
    appearance: {
      position: "bottom_right",
      theme: {
        buttonColor: "#9333EA",
        backgroundColor: "#FFFFFF",
        textColor: "#1E2125",
      },
    },
    questions: [
      {
        id: QUESTION_IDS.provider_review_rating,
        position: 0,
        type: "rating",
        text: "How would you rate your visit with this provider?",
        lowLabel: "Very poor",
        highLabel: "Excellent",
        scoreWeights: [2, 4, 10, 30, 54],
      },
      {
        id: QUESTION_IDS.provider_review_recommend,
        position: 1,
        type: "yes_no",
        text: "Would you recommend this provider to friends or family?",
        yesWeight: 0.9,
      },
      {
        id: QUESTION_IDS.provider_review_comments,
        position: 2,
        type: "open_text",
        text: "Anything you'd like the care team to know?",
        placeholder: "Optional",
        samplePool: [
          "The provider listened carefully and explained everything.",
          "Wait time was longer than expected.",
          "Loved the bedside manner — felt heard.",
          "Front desk was helpful with insurance questions.",
        ],
      },
    ],
  };

  return [
    patientNps,
    appointmentExperience,
    insuranceFeedback,
    providerReviewDraft,
  ];
}

function pathMatches(survey: PreviewSurvey, path: string): boolean {
  const { trigger } = survey;
  // Event-mode previews don't gate impressions on URL — the synthetic
  // event itself is the trigger. (Preview impression generation is
  // page-view driven today, so event-mode surveys produce zero
  // impressions until the preview graph adds event-driven impression
  // generation. That's acceptable — they still render in the UI for
  // configuration preview.)
  if (trigger.mode === "event" && trigger.patterns.length === 0) {
    return false;
  }
  if (trigger.patterns.length === 0) return true;
  return trigger.patterns.some((pat) => {
    if (!pat) return false;
    switch (trigger.matchType) {
      case "exact":
        return path === pat;
      case "starts_with":
        return path.startsWith(pat);
      case "contains":
        return path.includes(pat);
      case "wildcard":
        return matchesUrlPattern(path, pat);
    }
  });
}

interface ImpressionContext {
  session: PreviewSession;
  pageUrl: string;
  shownAt: number;
}

// For a given session, return the first matching page_view as the
// impression context, or null if the session is excluded by the survey's
// sessionFilter or no page_view matches.
function findImpressionContext(
  survey: PreviewSurvey,
  session: PreviewSession,
): ImpressionContext | null {
  if (survey.sessionFilter && !survey.sessionFilter(session)) return null;
  for (const evt of session.events) {
    if (evt.eventName !== "page_view") continue;
    if (pathMatches(survey, evt.path)) {
      return {
        session,
        pageUrl: evt.path,
        shownAt: evt.timestamp + (survey.idleTimeSeconds ?? 0) * 1000,
      };
    }
  }
  return null;
}

function pickWeightedIndex(rng: Rng, weights: readonly number[]): number {
  const total = weights.reduce((s, w) => s + w, 0);
  let roll = rng.next() * total;
  for (let i = 0; i < weights.length; i++) {
    roll -= weights[i];
    if (roll <= 0) return i;
  }
  return weights.length - 1;
}

function generateAnswer(
  rng: Rng,
  question: PreviewSurveyQuestion,
): PreviewSurveyAnswer {
  switch (question.type) {
    case "single_choice": {
      const idx = pickWeightedIndex(
        rng,
        question.choices.map((c) => c.weight),
      );
      return { questionId: question.id, value: question.choices[idx].label };
    }
    case "yes_no":
      return {
        questionId: question.id,
        value: rng.next() < question.yesWeight ? "Yes" : "No",
      };
    case "rating": {
      const idx = pickWeightedIndex(rng, question.scoreWeights);
      return { questionId: question.id, value: String(idx + 1) };
    }
    case "nps": {
      const idx = pickWeightedIndex(rng, question.scoreWeights);
      return { questionId: question.id, value: String(idx) };
    }
    case "open_text":
      return {
        questionId: question.id,
        value: question.samplePool[rng.int(question.samplePool.length)],
      };
  }
}

interface BuildArgs {
  rng: Rng;
  sessions: readonly PreviewSession[];
  devices: readonly PreviewDevice[];
  windowEnd: number;
}

interface BuildResult {
  surveys: PreviewSurvey[];
  surveyAggregates: Map<string, PreviewSurveyAggregate>;
}

export function buildSurveys({
  rng,
  sessions,
  devices,
  windowEnd,
}: BuildArgs): BuildResult {
  const surveys = buildSurveyDefinitions(windowEnd);
  const deviceById = new Map(devices.map((d) => [d.deviceID, d]));
  const aggregates = new Map<string, PreviewSurveyAggregate>();

  for (const survey of surveys) {
    const impressions: PreviewSurveyImpression[] = [];

    // Drafts haven't been published, so they have no live impressions or
    // responses — leave the aggregate empty so the demo reflects that.
    if (survey.status === "draft") {
      aggregates.set(survey.id, { surveyId: survey.id, impressions });
      continue;
    }

    for (const session of sessions) {
      const ctx = findImpressionContext(survey, session);
      if (!ctx) continue;

      // Throttle to the impression rate so not every matching session counts.
      if (rng.next() > PREVIEW_SURVEYS_BUILD_ARGS.IMPRESSION_RATE) continue;

      const becomesResponse = rng.next() < survey.responseRate;
      const device = deviceById.get(session.deviceID);
      const completionMs = becomesResponse
        ? ctx.shownAt + 8_000 + Math.floor(rng.next() * 60_000)
        : null;
      const answers: PreviewSurveyAnswer[] = becomesResponse
        ? survey.questions.map((q) => generateAnswer(rng, q))
        : [];

      impressions.push({
        surveyId: survey.id,
        sessionID: session.sessionID,
        deviceID: session.deviceID,
        identifier: device?.identifier ?? null,
        pageUrl: ctx.pageUrl,
        shownAt: ctx.shownAt,
        completedAt: completionMs,
        answers,
      });
    }

    aggregates.set(survey.id, { surveyId: survey.id, impressions });
  }

  return { surveys, surveyAggregates: aggregates };
}
